/**
 * Official Korean and Chinese names on the same English card record.
 *
 * These are an exact-match alternate, not a second page and not a fuzzy
 * field. The English `name` stays the title, canonical, and strongest
 * search field.
 */

const LOCALE_CODES = ['ko', 'zh'];

function faceOf(card, code) {
  return card?.locales?.[code] || null;
}

function trimmed(value) {
  return String(value || '').trim();
}

/**
 * Display labels for the printed alternate names (`name`, or `name - title`).
 * Bare titles are search keys only — they stay out of the visible list and
 * out of the page title.
 * @param {object | null | undefined} card
 * @returns {string[]}
 */
export function officialAlternateNames(card) {
  const names = [];
  const seen = new Set();
  for (const code of LOCALE_CODES) {
    const face = faceOf(card, code);
    const name = trimmed(face?.name);
    if (!name) continue;
    const title = trimmed(face?.title);
    const label = title ? `${name} - ${title}` : name;
    const key = label.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    names.push(label);
  }
  return names;
}

/**
 * True when `query` is an official Korean or Chinese name, title, or
 * `name - title`. Substrings and typos do not match.
 * @param {object | null | undefined} card
 * @param {string} query
 */
export function queryEqualsOfficialAlternate(card, query) {
  const q = trimmed(query).toLowerCase();
  if (!q) return false;
  for (const code of LOCALE_CODES) {
    const face = faceOf(card, code);
    const name = trimmed(face?.name).toLowerCase();
    const title = trimmed(face?.title).toLowerCase();
    if (name && q === name) return true;
    if (title && q === title) return true;
    if (name && title && q === `${name} - ${title}`) return true;
  }
  return false;
}
